/**
 * INTENT PRESERVATION — land the user on exactly what they clicked.
 *
 * The dashboard's scroll container is `<main>` inside DashboardLayout, NOT the window
 * (`overflow-y-auto` on a flex child). Every `window.scrollTo` in this app is therefore a
 * no-op on dashboard routes — which is why clicking a nav item or a tab left the user
 * staring at the previous scroll offset while the content silently swapped underneath.
 *
 * These helpers scroll the real container, focus the target (so keyboard and screen-reader
 * users land there too), and flash a ring so the eye catches where it went.
 */
export const APP_MAIN_ID = "app-main";

export function getScrollRoot(): HTMLElement | null {
  return document.getElementById(APP_MAIN_ID);
}

/**
 * The element that ACTUALLY scrolls for `el`. Do not assume `#app-main`: depending on the layout it is
 * height-constrained (and scrolls) or it grows and the document scrolls instead. Measured, not guessed.
 */
export function getScrollParent(el: Element | null): HTMLElement {
  let n: HTMLElement | null = (el as HTMLElement) ?? null;
  while (n && n !== document.body) {
    const cs = getComputedStyle(n);
    const scrolls = /(auto|scroll|overlay)/.test(cs.overflowY);
    if (scrolls && n.scrollHeight > n.clientHeight + 4) return n;
    n = n.parentElement;
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement;
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/** Focus a non-interactive region without adding it to the tab order, and flash it. */
export function focusAndFlash(el: HTMLElement | null): void {
  if (!el) return;
  try {
    if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
    // preventScroll: we have already positioned it; letting focus() re-scroll fights us.
    el.focus({ preventScroll: true });
    el.classList.add("mf-landed");
    window.setTimeout(() => el.classList.remove("mf-landed"), 1500);
  } catch {
    /* focus/flash is polish; the scroll already happened */
  }
}

/**
 * Bring `target` into view inside the dashboard scroll container and focus it.
 * `toTop` scrolls the container to its top instead (a fresh route starts at its own top).
 */
export function landOn(target?: Element | null, opts?: { toTop?: boolean }): void {
  const el = (target as HTMLElement | null) ?? getScrollRoot();
  if (!el) return;
  const root = getScrollParent(el);
  const behavior: ScrollBehavior = prefersReducedMotion() ? "auto" : "smooth";
  if (opts?.toTop) {
    root.scrollTo({ top: 0, behavior });
  } else {
    // Position relative to whatever actually scrolls. For the document, getBoundingClientRect is already
    // viewport-relative, so the container offset is 0.
    const isDoc = root === document.documentElement || root === document.body || root === document.scrollingElement;
    const containerTop = isDoc ? 0 : root.getBoundingClientRect().top;
    const top = root.scrollTop + el.getBoundingClientRect().top - containerTop - 12;
    root.scrollTo({ top: Math.max(0, top), behavior });
  }
  focusAndFlash(el);
}

/** Wait for the new subtree to render (state flush + layout) before measuring it. */
export function afterPaint(fn: () => void): void {
  try {
    requestAnimationFrame(() => requestAnimationFrame(fn));
  } catch {
    fn();
  }
}

/**
 * A tab click must SHOW that tab's content, in view. Radix swaps the panel, but the panel can
 * sit off-screen when the tab strip is scrolled down the page. Land on whichever panel is now active.
 */
export function landOnActiveTabPanel(container?: Element | null): void {
  afterPaint(() => {
    const scope: ParentNode = container ?? getScrollRoot() ?? document;
    const panel = scope.querySelector<HTMLElement>('[role="tabpanel"][data-state="active"]');
    if (panel) landOn(panel);
  });
}

/** Land on the current route's own landing target: an explicit `[data-landing]`, else its `h1`. */
export function landOnRouteTarget(): void {
  // Routes are lazy-loaded: two rAFs are not enough for the new view's <h1> to exist. Poll briefly,
  // then land on whatever we have. Without this we focus the empty shell and the ring never appears
  // on the heading the user navigated to.
  const deadline = Date.now() + 1200;
  const attempt = () => {
    const root = getScrollRoot();
    if (!root) return;
    const target =
      root.querySelector<HTMLElement>("[data-landing]") ?? root.querySelector<HTMLElement>("h1");
    if (!target && Date.now() < deadline) {
      afterPaint(attempt);
      return;
    }
    const scroller = getScrollParent(target ?? root);
    // A new route starts at its own top — leaving the user at the OLD offset is the bug.
    scroller.scrollTo({ top: 0, behavior: "auto" });
    focusAndFlash(target ?? root);
  };
  afterPaint(attempt);
}
